import { FormEvent, useEffect, useState } from "react";
import { Alert, Button, CircularProgress, TextField } from "@mui/material";
import { authentication } from "@microsoft/teams-js";
import { getTeamsSsoToken } from "../hooks/teamsSso";
import { TeamsUserData } from "../hooks/useTeamsUser";
import { useTranslation } from "../i18n/LocaleContext";
import {
  buildApiUrl,
  buildPopupUrl,
  clearBrowserQuery,
  OnboardingQueryState,
  parseOnboardingQuery,
  readResponseError,
} from "./consentFlow";
import SupportLink from "./SupportLink";
import AppFooter from "./AppFooter";
import "./OnboardingScreen.css";
// .app-footer / .blocked-state-support vivem aqui, como em BlockedStateScreen.
import "./SoftphoneDashboard.css";

type OnboardingScreenProps = {
  teamsUser?: TeamsUserData | null;
};

function syncBrowserQuery(queryState: OnboardingQueryState) {
  if (typeof window === "undefined") {
    return;
  }

  const params = new URLSearchParams();
  if (queryState.adminConsent) params.set("admin_consent", queryState.adminConsent);
  if (queryState.tenant) params.set("tenant", queryState.tenant);
  if (queryState.state) params.set("state", queryState.state);
  if (queryState.scope) params.set("scope", queryState.scope);
  if (queryState.error) params.set("error", queryState.error);
  if (queryState.errorDescription) params.set("error_description", queryState.errorDescription);
  if (queryState.errorUri) params.set("error_uri", queryState.errorUri);

  const nextQuery = params.toString();
  const nextUrl = nextQuery ? `${window.location.pathname}?${nextQuery}${window.location.hash}` : `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState({}, document.title, nextUrl);
}

export default function OnboardingScreen({ teamsUser }: OnboardingScreenProps) {
  const { t } = useTranslation();
  const [queryState, setQueryState] = useState<OnboardingQueryState>(() => parseOnboardingQuery());
  const [statusMessage, setStatusMessage] = useState("");
  const [statusIsError, setStatusIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [associationSucceeded, setAssociationSucceeded] = useState(false);
  const [tenantId, setTenantId] = useState(queryState.tenant);
  const [clientName, setClientName] = useState("");

  const isConsentAccepted = queryState.adminConsent === "true";
  const showErrorState = queryState.hasError || (queryState.hasConsentParam && !isConsentAccepted);

  useEffect(() => {
    if (isConsentAccepted && queryState.tenant) {
      setTenantId(queryState.tenant);
    }
  }, [isConsentAccepted, queryState.tenant]);

  function resetToIntro() {
    clearBrowserQuery();
    const nextQueryState = parseOnboardingQuery(window.location.search);
    setQueryState(nextQueryState);
    setAssociationSucceeded(false);
    setLoading(false);
    setClientName("");
    setSessionExpired(false);
    setStatusMessage("");
    setStatusIsError(false);
    setTenantId(nextQueryState.tenant);
  }

  // Saída da tela de cadastro submetido: recarrega para o check-in decidir o estado real
  // (aprovação pendente, no caso normal). Mesmo gesto do sucesso de PermissionsRenewalScreen.
  function handleContinue() {
    clearBrowserQuery();
    window.location.reload();
  }

  async function handleConsent() {
    setLoading(true);
    setStatusMessage(t("onboarding.status.openingConsentWindow"));
    setStatusIsError(false);

    try {
      const popupResult = await authentication.authenticate({
        url: buildPopupUrl(),
        width: 600,
        height: 720,
      });

      const nextQueryState = JSON.parse(popupResult) as OnboardingQueryState;
      syncBrowserQuery(nextQueryState);
      setAssociationSucceeded(false);
      setLoading(false);
      setQueryState(nextQueryState);
      setSessionExpired(false);
      setStatusMessage(
        nextQueryState.hasError || (nextQueryState.hasConsentParam && nextQueryState.adminConsent !== "true")
          ? t("onboarding.status.consentFailed")
          : t("onboarding.status.consentReceived")
      );
      setTenantId(nextQueryState.tenant || "");
    } catch (error) {
      setStatusMessage(t("onboarding.status.genericError", { message: error instanceof Error ? error.message : String(error) }));
      setStatusIsError(true);
      setLoading(false);
    }
  }

  async function handleAssociationSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatusMessage("");
    setStatusIsError(false);
    setSessionExpired(false);

    if (!clientName.trim()) {
      setStatusMessage(t("onboarding.status.fillClientName"));
      return;
    }

    setLoading(true);

    try {
      const accessToken = await getTeamsSsoToken();
      const response = await fetch(buildApiUrl("/api/bearer/tenant"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          admin_consent: queryState.adminConsent,
          scope: queryState.scope,
          state: queryState.state,
          tenantId: tenantId,
          clientName: clientName.trim(),
          tenantAdminId: teamsUser?.userId || "",
          tenantAdminName: teamsUser?.displayName || "",
          tenantAdminEmail: teamsUser?.userPrincipalName || teamsUser?.loginHint || "",
        }),
      });

      if (response.status === 401) {
        setSessionExpired(true);
        setStatusMessage(t("onboarding.status.sessionExpired"));
        setStatusIsError(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(await readResponseError(response));
      }

      setAssociationSucceeded(true);
      setStatusMessage(t("onboarding.status.associationSuccess"));
    } catch (error) {
      setStatusMessage(t("onboarding.status.genericError", { message: error instanceof Error ? error.message : String(error) }));
      setStatusIsError(true);
    } finally {
      setLoading(false);
    }
  }

  if (associationSucceeded) {
    return (
      <div className="softphone-shell onboarding-shell">
        <article className="status-card onboarding-card">
          <div className="onboarding-card-header">
            <div>
              <p className="eyebrow">{t("onboarding.eyebrow")}</p>
              <h1 className="onboarding-title">{t("onboarding.completed.title")}</h1>
              <p className="onboarding-card-copy">
                {t("onboarding.completed.copy")}
              </p>
            </div>
          </div>

          <div className="onboarding-actions">
            <Button variant="contained" onClick={handleContinue}>
              {t("onboarding.completed.continueButton")}
            </Button>
            <SupportLink className="app-footer-link blocked-state-support" />
          </div>
        </article>

        <AppFooter />
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div className="softphone-shell onboarding-shell">
        <section className="hero-card onboarding-hero">
          <div className="hero-content">
            <p className="eyebrow">{t("onboarding.eyebrow")}</p>
            <h1>{t("onboarding.sessionExpired.title")}</h1>
            <p className="hero-copy">
              {t("onboarding.sessionExpired.copy")}
            </p>
          </div>
        </section>

        <article className="status-card onboarding-card">
          <div className="onboarding-card-header">
            <div>
              <h2>{t("onboarding.sessionExpired.cardTitle")}</h2>
              <p className="onboarding-card-copy">
                {statusMessage || t("onboarding.sessionExpired.defaultDetail")}
              </p>
            </div>
          </div>

          <div className="onboarding-actions">
            <Button variant="contained" onClick={resetToIntro}>
              {t("onboarding.sessionExpired.retryButton")}
            </Button>
          </div>
        </article>
      </div>
    );
  }

  if (showErrorState) {
    return (
      <div className="softphone-shell onboarding-shell">
        <article className="status-card onboarding-card">
          <div className="onboarding-card-header">
            <div>
              <p className="eyebrow">{t("onboarding.eyebrow")}</p>
              <h1 className="onboarding-title">{t("onboarding.errorState.title")}</h1>
              <p className="onboarding-card-copy">
                {t("onboarding.errorState.copy")}
              </p>
            </div>
          </div>

          <div className="onboarding-form">
            <Alert severity="error">
              <strong>{t("onboarding.errorState.codeLabel")}</strong> {queryState.error || t("onboarding.errorState.defaultCode")}
            </Alert>
            {queryState.errorDescription ? (
              <Alert severity="warning">
                <pre className="onboarding-error-pre">{queryState.errorDescription}</pre>
              </Alert>
            ) : null}
            {queryState.errorUri ? (
              <Alert severity="info">
                {t("onboarding.errorState.moreDetails")} <a href={queryState.errorUri} target="_blank" rel="noreferrer">{queryState.errorUri}</a>
              </Alert>
            ) : null}
          </div>

          <div className="onboarding-actions">
            <Button variant="contained" onClick={resetToIntro}>
              {t("onboarding.errorState.retryButton")}
            </Button>
          </div>
        </article>
      </div>
    );
  }

  if (!queryState.hasConsentParam) {
    return (
      <div className="softphone-shell onboarding-shell">
        <article className="status-card onboarding-card">
          <div className="onboarding-card-header">
            <div>
              <p className="eyebrow">{t("onboarding.intro.eyebrow")}</p>
              <h1>{t("onboarding.intro.title")}</h1>
              <p className="onboarding-card-copy">
                {t("onboarding.intro.copy1")}
              </p>
              <br />
              <h2>{t("onboarding.intro.consentTitle")}</h2>
              <p className="onboarding-card-copy">
                {t("onboarding.intro.copy2")}
              </p>
            </div>
          </div>

          {statusMessage ? (
            <Alert severity={statusIsError ? "error" : "info"} className="onboarding-status">
              {statusMessage}
            </Alert>
          ) : null}

          <div className="onboarding-actions">
            <Button variant="contained" onClick={handleConsent} disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : t("onboarding.intro.consentButton")}
            </Button>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="softphone-shell onboarding-shell">
      <article className="status-card onboarding-card">
        <div className="onboarding-card-header">
          <div>
            <p className="eyebrow">{t("onboarding.eyebrow")}</p>
            <h1 className="onboarding-title">{t("onboarding.association.title")}</h1>
            <p className="onboarding-card-copy">
              {t("onboarding.association.copy")}
            </p>
          </div>
        </div>

        <form className="onboarding-form" onSubmit={handleAssociationSubmit}>
          <div className="onboarding-field">
            <label className="onboarding-field-label" htmlFor="onboarding-tenant-id">
              {t("onboarding.association.tenantIdLabel")}
            </label>
            <div id="onboarding-tenant-id" className="onboarding-readonly">
              {tenantId || t("onboarding.association.tenantIdEmpty")}
            </div>
          </div>

          <div className="onboarding-field">
            <label className="onboarding-field-label" htmlFor="onboarding-client-name">
              {t("onboarding.association.clientNameLabel")}
            </label>
            <TextField
              autoComplete="off"
              disabled={loading}
              error={!clientName.trim()}
              fullWidth
              helperText={!clientName.trim() ? t("onboarding.status.fillClientName") : ""}
              id="onboarding-client-name"
              onChange={(event) => setClientName(event.target.value)}
              placeholder={t("onboarding.association.clientNamePlaceholder")}
              value={clientName}
            />
          </div>

          {statusMessage ? (
            <Alert
              className="onboarding-status"
              severity={statusIsError ? "error" : "info"}
            >
              {statusMessage}
            </Alert>
          ) : null}

          <div className="onboarding-actions">
            <Button disabled={loading || !clientName.trim()} type="submit" variant="contained">
              {loading ? <CircularProgress size={20} color="inherit" /> : t("onboarding.association.submitButton")}
            </Button>
            <Button disabled={loading} onClick={resetToIntro} type="button" variant="outlined">
              {t("onboarding.association.restartButton")}
            </Button>
          </div>
        </form>
      </article>
    </div>
  );
}
